import { Workbook } from 'exceljs'
import moment from 'moment'

import { Activity, Observation, ObservationStatus } from '@types'

interface ObservationExcelFilters {
  companyId?: string
  projectFileId?: string
  tradeKey?: string
  phaseKey?: string
  observationStatus?: string
  createdDate?: string
  limitDate?: string
}

const downloadExcel = (buffer: Blob, filename: string) => {
  const url = window.URL.createObjectURL(buffer)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export const generateExcelFile = async (
  headers: string[],
  observations: Observation[],
  name: string,
  getStatusTranslationKey: (status: ObservationStatus) => string,
  filters: ObservationExcelFilters,
): Promise<void> => {
  try {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Observations')

    workbook.calcProperties.fullCalcOnLoad = true

    worksheet.columns = [
      { width: 5 },
      { width: 20 },
      { width: 20 },
      { width: 17 },
      { width: 25 },
      { width: 17 },
      { width: 30 },
      { width: 17 },
      { width: 15 },
      { width: 30 },
      { width: 30 },
      { width: 35 },
    ]

    const getValidationDates = (observationActivities: Activity[]) => {
      // Objet pour stocker les dates de validation par type de statut
      const validationDates: {
        readerDate?: Date
        customerDate?: Date
        adminDate?: Date
      } = {}

      // Pour status_validated_by_reader
      const readerActivities = observationActivities
        .filter((activity) => activity.status === 'status_validated_by_reader')
        .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())

      if (readerActivities.length > 0) {
        validationDates.readerDate = new Date(readerActivities[0].createdDate)
      }

      // Pour status_validated_by_customer
      const customerActivities = observationActivities
        .filter((activity) => activity.status === 'status_validated_by_customer')
        .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())

      if (customerActivities.length > 0) {
        validationDates.customerDate = new Date(customerActivities[0].createdDate)
      }

      // Pour status_validated_by_admin
      const adminActivities = observationActivities
        .filter((activity) => activity.status === 'status_validated_by_admin')
        .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())

      if (adminActivities.length > 0) {
        validationDates.adminDate = new Date(adminActivities[0].createdDate)
      }

      return validationDates
    }

    const tableRows = observations
      .filter(({ trade, company, phase, status, projectFile, createdDate, limitDate }) => {
        if (filters.companyId && company?.id !== filters.companyId) {
          return false
        }

        if (filters.tradeKey && trade.key !== filters.tradeKey) {
          return false
        }

        // Filtre par phase (phaseKey)
        if (filters.phaseKey && phase?.key !== filters.phaseKey) {
          return false
        }

        // Filtre par statut d'observation (observationStatus)
        if (filters.observationStatus && status !== filters.observationStatus) {
          return false
        }

        // Filtre par fichier de projet (projectFileId)
        if (filters.projectFileId && projectFile?.id !== filters.projectFileId) {
          return false
        }

        // Filtre par date de création (vérifie si la date est postérieure à la date minimale)
        if (filters.createdDate) {
          const filterDate = new Date(filters.createdDate)
          const observationDate = new Date(createdDate)
          if (observationDate < filterDate) {
            return false
          }
        }

        // Filtre par date limite (vérifie si la date limite est antérieure à la date maximale)
        if (filters.limitDate && limitDate) {
          const filterDate = new Date(filters.limitDate)
          const observationLimitDate = new Date(limitDate)
          if (observationLimitDate > filterDate) {
            return false
          }
        }

        // Si tous les filtres sont passés, on garde l'observation
        return true
      })
      .map((observation) => {
        const validationDates = getValidationDates(observation.activities)
        return [
          observation.idNumber,
          observation.name,
          observation.trade?.label || '',
          observation.company?.name || '',
          observation.projectFile?.name || '',
          observation.phase?.label || '',
          getStatusTranslationKey(observation.status),
          moment(observation.createdDate).format('L'),
          observation.limitDate ? moment(observation.limitDate).format('L') : '',
          validationDates.readerDate ? moment(validationDates.readerDate).format('L') : '',
          validationDates.adminDate ? moment(validationDates.adminDate).format('L') : '',
          validationDates.customerDate ? moment(validationDates.customerDate).format('L') : '',
        ]
      })

    // Ajout de la table avec style
    worksheet.addTable({
      name: 'ObservationsTable',
      ref: 'A1',
      headerRow: true,
      totalsRow: false,
      style: {
        theme: 'TableStyleMedium1',
        showRowStripes: true,
      },
      columns: headers.map((header) => ({
        name: header,
        filterButton: true,
      })),
      rows: tableRows,
    })

    // Style des lignes de données
    tableRows.forEach((_, index) => {
      const rowNumber = index + 2
      const row = worksheet.getRow(rowNumber)

      // Application du style sur toutes les colonnes
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: index % 2 === 0 ? 'FFFFFF' : 'F9F9F9' },
        }
        cell.font = { name: 'Calibri' }
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'left',
        }
      })
    })

    // Style de l'en-tête
    const headerRow = worksheet.getRow(1)
    headerRow.height = 20
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F3F4F6' },
      }
      cell.font = { name: 'Calibri', bold: true }
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'left',
      }
    })

    const timestamp = moment().format('YYYY-MM-DD_HH-mm')
    const filename = `${name}_${timestamp}.xlsx`

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    downloadExcel(blob, filename)
  } catch (error) {
    console.error('Erreur lors de la génération du fichier Excel:', error)
    throw error
  }
}
